import React, {useEffect, useState} from 'react';
import {View, Dimensions, StyleSheet} from 'react-native';
import {useSelector} from 'react-redux';
import {ChartLoading, NoDataDisplay, GridLines, YAxisLabels} from './ChartComponents';
import {
  calculateDailyChartDimensions,
  calculateOptimalDisplayTime,
  generateYAxisValues,
  processScanScoreStatistics
} from './chartUtils';
import AutoScrollWrapper from './AutoScrollWrapper';
import useChartAnimations from './useChartAnimations';
import {ChartContent} from './svgComponents';

const maxBarsToShow = 6;
const barGapRatio = 0.2;
const yAxisWidth = 50;
const segmentsCount = 6;
const labelSpaceHeight = 50;

const ScanToScoreChart = ({updateAutoTime}) => {
  const response = useSelector(state => state.statistics.scanScoreDailyList);
  const liveScanScoreList = useSelector(state => state.statistics.scanScoreList);

  const [containerDimensions, setContainerDimensions] = useState({
    width: Dimensions.get('window').width * 0.9,
    height: 450
  });
  const [dataList, setDataList] = useState([]);
  const [maxValue, setMaxValue] = useState(0);
  const [loading, setLoading] = useState(!response?.data?.statistics);
  const [barWidth, setBarWidth] = useState(80);
  const [spacing, setSpacing] = useState(20);
  const [chartWidth, setChartWidth] = useState(0);
  const [shouldScroll, setShouldScroll] = useState(false);

  const animations = useChartAnimations(dataList, 800, 300, 100);

  const onContainerLayout = event => {
    const {width, height} = event.nativeEvent.layout;
    setContainerDimensions({width, height});
  };

  useEffect(() => {
    const result = processScanScoreStatistics(response, liveScanScoreList);

    if (!result.success) {
      setLoading(false);
      return;
    }

    setDataList(result.dataList);
    setMaxValue(result.maxValue);

    if (result.dataList.length === 0) {
      setLoading(false);
      return;
    }

    const dims = calculateDailyChartDimensions(
      result.dataList,
      containerDimensions,
      maxBarsToShow,
      barGapRatio,
      yAxisWidth
    );
    setBarWidth(dims.barWidth);
    setSpacing(dims.spacing);
    setChartWidth(dims.chartWidth);
    setShouldScroll(dims.shouldScroll);

    if (updateAutoTime && typeof updateAutoTime === 'function') {
      const optimalTime = calculateOptimalDisplayTime(
        result.dataList,
        'daily',
        maxBarsToShow,
        dims.barWidth + dims.spacing
      );
      updateAutoTime(optimalTime);
    }
    setLoading(false);
  }, [
    response?.data?.statistics,
    response?.data?.user_details,
    liveScanScoreList,
    containerDimensions.width,
    containerDimensions.height
  ]);

  const chartContainerHeight = containerDimensions.height * 0.85;
  const adjustedChartHeight = chartContainerHeight - 10;

  const renderContent = () => {
    if (loading) {
      return <ChartLoading />;
    }

    if (dataList.length === 0) {
      return <NoDataDisplay />;
    }

    return (
      <View style={styles.mainContent}>
        <View style={styles.chartArea}>
          <View style={styles.chartRow}>
            <YAxisLabels
              values={generateYAxisValues(maxValue, segmentsCount)}
              chartHeight={adjustedChartHeight}
              segmentsCount={segmentsCount}
              containerStyle={{height: adjustedChartHeight}}
            />
            <AutoScrollWrapper
              enabled={shouldScroll}
              speed={35}
              pauseAtEnds={2500}
              showIndicators={shouldScroll}
              style={styles.chartBackground}>
              <View
                style={[
                  styles.svgContainer,
                  {height: adjustedChartHeight + labelSpaceHeight, width: chartWidth}
                ]}>
                <ChartContent
                  chartHeight={adjustedChartHeight}
                  chartWidth={chartWidth}
                  labelSpaceHeight={labelSpaceHeight}
                  segmentsCount={segmentsCount}
                  data={dataList}
                  barWidth={barWidth}
                  spacing={spacing}
                  maxValue={maxValue}
                  animations={animations}
                  GridLines={GridLines}
                  adaptiveValueFont={true}
                />
              </View>
            </AutoScrollWrapper>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container} onLayout={onContainerLayout}>
      {renderContent()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#191919',
    height: '90%'
  },
  mainContent: {
    flex: 1,
    width: '100%',
    height: '100%',
    paddingVertical: 10,
    paddingBottom: 40,
    paddingHorizontal: 5
  },
  chartArea: {
    flex: 1,
    height: '100%'
  },
  chartRow: {
    flexDirection: 'row',
    height: '100%'
  },
  chartBackground: {
    flex: 1,
    backgroundColor: '#191919'
  },
  svgContainer: {
    borderRadius: 3,
    overflow: 'hidden'
  }
});

export default ScanToScoreChart;
